import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import { Close as CloseIcon, BarChart as BarChartIcon } from "@mui/icons-material";
import { useIntl } from "react-intl";

import { AiUsageRecordRow } from "../types/MeGenTypes";
import { aggregateRecords, formatMetric, formatMoney, grandTotals, serviceLabelOf, toLocalDateTime } from "./aiUsagePivot";
import { buildPriceLineInfos, makeRecordCostFn, makeRecordCostInfoFn, usageTextOf } from "./aiModelCost";
import { useAiModelRates } from "./useAiModelRates";
import AiUsagePagination, { pageOf } from "./AiUsagePagination";
import AiUsageModelLabel from "./AiUsageModelLabel";
import { usageModelKeyOf } from "./aiUsageModels";
import FfmpegUsageSummary from "./FfmpegUsageSummary";
import { compareUsageRecordsNewestFirst, isUsageError } from "./ffmpegUsage";
import { FfmpegAllocationCost, FfmpegAllocationTime } from "./FfmpegAllocationValue";

/**
 * Modal drill-down: แสดง "ส่วนประกอบ" ของแท่ง/ชิ้นกราฟที่ถูกคลิก
 * รับ records ที่ filter มาแล้ว (client-side) → สรุปยอด + แยกตาม model + แยกตาม thread + รายการ log
 */

// ─── Styled ──────────────────────────────────────────────────────────────────

const Overlay = styled(Box)({
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(15, 23, 42, 0.5)",
  backdropFilter: "blur(4px)",
});

const ModalCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "1rem",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  width: "100%",
  maxWidth: "820px",
  maxHeight: "85vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

const SectionLabel = styled(Typography)({
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#94a3b8",
  marginBottom: "0.5rem",
});

const thTd: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: "0.75rem",
  borderBottom: "1px solid #f1f5f9",
  textAlign: "left",
  whiteSpace: "nowrap",
};
const thStyle: React.CSSProperties = { ...thTd, fontSize: "0.68rem", color: "#64748b", backgroundColor: "#f8fafc" };
const numTd: React.CSSProperties = { ...thTd, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const rowUsageText = (r: AiUsageRecordRow): string => {
  const parts: string[] = [];
  if (r.image_count) parts.push(`${r.image_count} รูป`);
  if (r.video_duration) parts.push(`วิดีโอ ${formatMetric(r.video_duration)} วิ`);
  if (r.audio_duration) parts.push(`เสียง ${formatMetric(r.audio_duration)} วิ`);
  if (r.input_tokens || r.output_tokens)
    parts.push(`tokens ${formatMetric(r.input_tokens)}→${formatMetric(r.output_tokens)}`);
  if (r.resolution) parts.push(r.resolution);
  return parts.join(" · ") || "-";
};

// ─── Component ───────────────────────────────────────────────────────────────

type ModAiUsageBreakdownProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  records: AiUsageRecordRow[];
  userNameMap?: Record<string, string>;
  threadTitles?: Record<string, string>;
  onClose: () => void;
  onOpenThread?: (threadId: string) => void;
};

const ModAiUsageBreakdown: React.FC<ModAiUsageBreakdownProps> = (props) => {
  const intl = useIntl();
  const { records } = props;
  const [recordPage, setRecordPage] = useState(0);
  const [threadPage, setThreadPage] = useState(0);
  useEffect(() => { setRecordPage(0); setThreadPage(0); }, [records, props.open]);
  const orderedRecords = useMemo(() => [...records].sort(compareUsageRecordsNewestFirst), [records]);

  // ราคาต่อ model ชุดเดียวกับ dashboard/modal run (override เก็บใน localStorage ผ่าน useAiModelRates)
  // ที่นี่แสดงอย่างเดียว แก้ราคาได้ที่แท็บ "ราคา Model"
  const rates = useAiModelRates();
  const costOf = useMemo(() => makeRecordCostFn(rates.overrides), [rates.overrides]);
  const costInfoOf = useMemo(() => makeRecordCostInfoFn(rates.overrides), [rates.overrides]);
  const priceLineInfos = useMemo(() => buildPriceLineInfos(records, rates.overrides), [records, rates.overrides]);

  const totals = useMemo(() => grandTotals(records, costOf), [records, costOf]);
  const byModel = useMemo(
    () =>
      aggregateRecords(records, usageModelKeyOf, costOf).sort(
        (a, b) => b.cost_usd - a.cost_usd || b.calls - a.calls
      ),
    [records, costOf]
  );
  const byThread = useMemo(
    () =>
      aggregateRecords(records.filter((r) => r.thread_id), (r) => r.thread_id as string, costOf).sort(
        (a, b) => b.cost_usd - a.cost_usd || b.calls - a.calls
      ),
    [records, costOf]
  );

  if (!props.open) return null;

  const nameOf = (userId: string | null | undefined): string =>
    (userId && props.userNameMap?.[userId]) || userId || "-";
  const titleOf = (threadId: string): string | undefined => props.threadTitles?.[threadId];

  const chips: { label: string; value: number; unit: string; danger?: boolean; format?: (v: number) => string }[] = [
    { label: "ค่าใช้จ่าย", value: totals.cost_usd, unit: "USD", format: formatMoney },
    { label: "Calls", value: totals.calls, unit: "ครั้ง" },
    { label: "รูปภาพ", value: totals.images, unit: "รูป" },
    { label: "วิดีโอ", value: totals.video_seconds, unit: "วินาที" },
    { label: "เสียง", value: totals.audio_seconds, unit: "วินาที" },
    { label: "Tokens เข้า", value: totals.input_tokens, unit: "tokens" },
    { label: "Tokens ออก", value: totals.output_tokens, unit: "tokens" },
    { label: "Errors", value: totals.errors, unit: "ครั้ง", danger: true },
  ];

  return (
    <Overlay id="megen-ai-usage-breakdown-modal" onClick={props.onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
          <BarChartIcon sx={{ color: "#db2777" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>{props.title}</Typography>
            {props.subtitle && (
              <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>{props.subtitle}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={props.onClose} id="megen-ai-usage-breakdown-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, overflowY: "auto" }}>
          <FfmpegUsageSummary records={records} costInfoOf={costInfoOf} />
          {/* Totals */}
          <SectionLabel>ยอดรวมของส่วนนี้</SectionLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {chips
              .filter((c) => c.value > 0 || c.label === "Calls" || c.format)
              .map((c) => (
                <Box key={c.label}
                  sx={{
                    px: 1.5, py: 0.75, borderRadius: "0.5rem",
                    backgroundColor: c.danger ? "#fef2f2" : c.format ? "#fdf2f8" : "#f8fafc",
                    border: `1px solid ${c.danger ? "#fecaca" : c.format ? "#f9a8d4" : "#e2e8f0"}`,
                    minWidth: "90px",
                  }}>
                  <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8" }}>{c.label}</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: c.danger ? "#dc2626" : c.format ? "#be185d" : "#1e293b" }}>
                    {c.format ? c.format(c.value) : `${formatMetric(c.value)} ${c.unit}`}
                  </Typography>
                </Box>
              ))}
          </Box>

          {/* By model */}
          {byModel.length > 1 && (
            <>
              <SectionLabel>แยกตาม AI Model</SectionLabel>
              <Box sx={{ overflowX: "auto", border: "1px solid #f1f5f9", borderRadius: "0.5rem", mb: 3 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "560px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Service</th>
                      <th style={thStyle}>Model</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Calls (ครั้ง)</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>ค่าใช้จ่าย (USD)</th>
                      <th style={thStyle}>ปริมาณที่ใช้</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byModel.map((m) => {
                      const [service, , model] = m.key.split("|");
                      const ffmpegResult = service === "ffmpeg" ? priceLineInfos.get(`${m.key}|hour`)?.result : undefined;
                      return (
                        <tr key={m.key}>
                          <td style={thTd}>{serviceLabelOf(service)}</td>
                          <td style={{ ...thTd, fontFamily: "monospace" }}>{model}</td>
                          <td style={numTd}>{formatMetric(m.calls)}</td>
                          <td style={{ ...numTd, fontWeight: 600 }}>{ffmpegResult ? <FfmpegAllocationCost result={ffmpegResult} /> : formatMoney(m.cost_usd)}</td>
                          <td style={thTd}>{ffmpegResult ? <FfmpegAllocationTime result={ffmpegResult} seconds={m.process_seconds} /> : usageTextOf(m)}</td>
                          <td style={{ ...numTd, color: m.errors ? "#dc2626" : "#94a3b8", fontWeight: m.errors ? 700 : 400 }}>
                            {m.errors}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </>
          )}

          {/* By thread */}
          {byThread.length > 0 && (
            <>
              <SectionLabel>แยกตาม Video / Thread (คลิกเพื่อดูรายละเอียด run)</SectionLabel>
              <AiUsagePagination total={byThread.length} page={threadPage} onChange={setThreadPage} />
              <Box sx={{ overflowX: "auto", border: "1px solid #f1f5f9", borderRadius: "0.5rem", mb: 3 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "560px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Video</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Calls (ครั้ง)</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>ค่าใช้จ่าย (USD)</th>
                      <th style={thStyle}>ปริมาณที่ใช้</th>
                      <th style={thStyle}>Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageOf(byThread, threadPage).map((t) => (
                      <tr key={t.key} style={{ cursor: props.onOpenThread ? "pointer" : "default" }}
                        onClick={() => props.onOpenThread?.(t.key)}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fdf2f8")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}>
                        <td style={thTd}>
                          <span style={{ fontWeight: 600, color: "#334155" }}>
                            {titleOf(t.key) || "(ไม่มีชื่อ)"}
                          </span>
                          <span style={{ fontFamily: "monospace", color: "#94a3b8", marginLeft: 6, fontSize: "0.68rem" }}>
                            {t.key.slice(0, 8)}…
                          </span>
                        </td>
                        <td style={numTd}>{formatMetric(t.calls)}</td>
                        <td style={{ ...numTd, fontWeight: 600 }}>{formatMoney(t.cost_usd)}</td>
                        <td style={thTd}>{usageTextOf(t)}</td>
                        <td style={thTd}>{t.users.map(nameOf).join(", ") || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </>
          )}

          {/* Records */}
          <SectionLabel>
            รายการ (ใหม่ → เก่า)
          </SectionLabel>
          <AiUsagePagination total={records.length} page={recordPage} onChange={setRecordPage} />
          <Box sx={{ maxHeight: "240px", overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: "0.5rem" }}>
            {pageOf(orderedRecords, recordPage)
              .map((r) => (
                <Box key={r.id}
                  sx={{
                    display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1,
                    px: 1.5, py: 0.75, borderBottom: "1px solid #f8fafc",
                    backgroundColor: isUsageError(r) ? "#fef2f2" : "transparent",
                  }}>
                  <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#64748b", whiteSpace: "nowrap" }}>
                    {toLocalDateTime(r.created_at)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155" }}>
                    {serviceLabelOf(r.service)} · <AiUsageModelLabel provider={r.provider} model={r.model} videoDuration={r.service === "video" ? r.video_duration : undefined} />
                  </Typography>
                  {r.user_id && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>โดย {nameOf(r.user_id)}</Typography>
                  )}
                  <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>{rowUsageText(r)}</Typography>
                  {r.ffmpeg_task && <Typography variant="caption">{intl.formatMessage({ id: "megen.cost.ffmpegAllocated" }, { seconds: r.duration_ms === null ? "—" : formatMetric(r.duration_ms / 1000) })}</Typography>}
                  {r.status !== "success" && (
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: isUsageError(r) ? "#dc2626" : "#b45309" }}>{r.ffmpeg_task?.status || r.status}</Typography>
                  )}
                </Box>
              ))}
            {records.length === 0 && (
              <Typography sx={{ p: 2, fontSize: "0.8rem", color: "#94a3b8" }}>ไม่มีข้อมูล</Typography>
            )}
          </Box>
        </Box>
      </ModalCard>
    </Overlay>
  );
};

export default ModAiUsageBreakdown;
